/**
 * KYC verification error codes returned by the backend in `ErrorObject.code`.
 *
 * Mirrors io.relaypay.api.exception.KycVerificationException.KycVerificationErrorCode in
 * relaypay-api — kept in sync deliberately, since there is no generated client enum and the
 * codes are otherwise scattered as string literals across the app.
 *
 * The backend `message` is a developer/log string (and may contain internal ids), so it must
 * not be shown to users. Derive user-facing copy from the `code` via `getKycErrorMessage`.
 */
export const KycErrorCode = {
  StatusError: 'KYC_VERIFICATION_STATUS_ERROR',
  ActionError: 'KYC_VERIFICATION_ACTION_ERROR',
  AttemptLimit: 'KYC_VERIFICATION_ATTEMPT_LIMIT',
  CodeNotMatched: 'KYC_VERIFICATION_CODE_NOT_MATCHED',
  CodeExpired: 'KYC_VERIFICATION_CODE_EXPIRED',
  CheckIdNotPresent: 'KYC_VERIFICATION_CHECK_ID_NOT_PRESENT',
  MethodNotAllowed: 'KYC_VERIFICATION_METHOD_NOT_ALLOWED',
  InspectionIdNotPresent: 'KYC_VERIFICATION_INSPECTION_ID_NOT_PRESENT',
  KycStatusError: 'KYC_STATUS_ERROR',
  NetworkError: 'KYC_NETWORK_ERROR',
  UserNotFound: 'KYC_USER_NOT_FOUND',
  BuyCryptoNotFound: 'KYC_BUY_CRYPTO_NOT_FOUND',
  MfaDowngradeNotFound: 'KYC_MFA_DOWNGRADE_NOT_FOUND',
  UserError: 'KYC_USER_ERROR',
} as const;

export type KycErrorCodeValue = (typeof KycErrorCode)[keyof typeof KycErrorCode];

const DEFAULT_KYC_ERROR_MESSAGE = 'We could not verify your identity. Please complete verification and try again.';

// User-facing copy per code. Codes without an entry fall back to the default message.
const KYC_ERROR_MESSAGES: Partial<Record<KycErrorCodeValue, string>> = {
  [KycErrorCode.CheckIdNotPresent]: 'Please complete identity verification to continue your purchase.',
  [KycErrorCode.CodeNotMatched]: 'Please complete identity verification to continue your purchase.',
  [KycErrorCode.CodeExpired]: 'Your verification has expired. Please verify again to continue.',
  [KycErrorCode.AttemptLimit]: 'You have reached the verification attempt limit. Please contact support to continue.',
  [KycErrorCode.NetworkError]: 'We could not reach our verification provider. Please try again shortly.',
};

export function getKycErrorMessage(code?: string | null): string {
  if (!code) return DEFAULT_KYC_ERROR_MESSAGE;
  return KYC_ERROR_MESSAGES[code as KycErrorCodeValue] ?? DEFAULT_KYC_ERROR_MESSAGE;
}
